import { useEffect, useId, useRef, useState } from "react";

import type {
  DocumentWorkflowFileVersion,
  DocumentWorkflowSignature,
} from "../entities/document-workflow";
import type { CadesProfile, SigningCertificate } from "../cryptopro";
import { certificateName, displayDateTime } from "./fileLabels";
import { SignatureDetails } from "./SignatureDetails";
import type { ShellServices } from "./services";
import { failureMessages, workingSteps } from "./signingMessages";
import { useVersionSigning } from "./useVersionSigning";

function CertificateOption({
  certificate,
  checked,
  onSelect,
  name,
}: {
  certificate: SigningCertificate;
  checked: boolean;
  onSelect: () => void;
  name: string;
}) {
  const id = useId();

  return (
    <li className="signing-panel__certificate">
      <input
        id={id}
        type="radio"
        name={name}
        checked={checked}
        onChange={onSelect}
      />
      <label htmlFor={id}>
        <strong>{certificateName(certificate.subject)}</strong>
        <small>Издатель: {certificateName(certificate.issuer)}</small>
        <small>
          Действует до {displayDateTime(certificate.validTo)} · отпечаток …
          {certificate.id.slice(-8)}
        </small>
      </label>
    </li>
  );
}

/**
 * Подписание одной версии: выбор сертификата и ход операции.
 *
 * Панель ничего не решает сама — состояние и порядок шагов задаёт сценарий
 * `useVersionSigning`, а здесь они только показываются.
 */
export function VersionSigningPanel({
  services,
  version,
  profile,
  requestTtlSeconds,
  onSigned,
  onClose,
}: {
  services: ShellServices;
  version: Pick<
    DocumentWorkflowFileVersion,
    "id" | "sha256" | "version_number"
  >;
  profile: CadesProfile;
  requestTtlSeconds: number;
  onSigned: (signature: DocumentWorkflowSignature) => void;
  onClose: () => void;
}) {
  const { state, start, sign, resend, reset } = useVersionSigning({
    services,
    version,
    profile,
    capabilities: { request_ttl_seconds: requestTtlSeconds },
    onSigned,
  });
  const [selected, setSelected] = useState<string | null>(null);
  const groupName = useId();
  const started = useRef(false);

  useEffect(() => {
    // Плагин проверяется только после нажатия «Подписать»: панель открывается
    // по действию человека, а не при показе списка версий.
    if (started.current) return;
    started.current = true;
    void start();
  }, [start]);

  const certificates = state.phase === "choose" ? state.certificates : [];
  const chosen =
    selected !== null && certificates.some((item) => item.id === selected)
      ? selected
      : (certificates[0]?.id ?? null);

  const close = () => {
    reset();
    onClose();
  };

  return (
    <section
      className="signing-panel"
      aria-label={`Подписание версии ${version.version_number}`}
    >
      <h4>Подписание версии {version.version_number}</h4>
      <p className="dw-secondary">
        Подпись создаётся в вашем браузере плагином КриптоПро: закрытый ключ не
        покидает его. На сервер уходит только готовая отделённая подпись.
      </p>

      {state.phase === "working" ? (
        <p role="status" aria-busy="true">
          {workingSteps[state.step]}
        </p>
      ) : null}

      {state.phase === "choose" ? (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (chosen !== null) void sign(chosen);
          }}
        >
          <fieldset className="signing-panel__certificates">
            <legend>Сертификат для подписи</legend>
            <ul>
              {certificates.map((certificate) => (
                <CertificateOption
                  key={certificate.id}
                  certificate={certificate}
                  name={groupName}
                  checked={certificate.id === chosen}
                  onSelect={() => setSelected(certificate.id)}
                />
              ))}
            </ul>
          </fieldset>
          <div className="inline-actions">
            <button
              className="button button--primary button--compact"
              type="submit"
              disabled={chosen === null}
            >
              Подписать выбранным сертификатом
            </button>
            <button
              className="button button--text button--compact"
              type="button"
              onClick={close}
            >
              Отмена
            </button>
          </div>
        </form>
      ) : null}

      {state.phase === "done" ? (
        <div role="status" className="signing-panel__result">
          <p>
            <strong>Версия подписана.</strong> Сервер принял подпись и выполнил
            проверку.
          </p>
          <SignatureDetails signature={state.signature} />
          <div className="inline-actions">
            <button
              className="button button--secondary button--compact"
              type="button"
              onClick={close}
            >
              Закрыть
            </button>
          </div>
        </div>
      ) : null}

      {state.phase === "failed" ? (
        <div
          role="alert"
          className={`signing-panel__failure signing-panel__failure--${state.kind}`}
          data-failure={state.kind}
        >
          <p>
            <strong>{failureMessages[state.kind].title}</strong>
          </p>
          <p>{failureMessages[state.kind].text}</p>
          {state.prepared ? (
            <p>
              Подпись уже создана в браузере и не потеряна: её можно отправить
              ещё раз, пока действует запрос.
            </p>
          ) : null}
          <div className="inline-actions">
            {state.prepared ? (
              <button
                className="button button--primary button--compact"
                type="button"
                onClick={() => void resend()}
              >
                Отправить подпись ещё раз
              </button>
            ) : null}
            {state.kind === "capability_disabled" ||
            state.kind === "permission_denied" ? null : (
              <button
                className="button button--secondary button--compact"
                type="button"
                onClick={() => {
                  setSelected(null);
                  void start();
                }}
              >
                {state.kind === "plugin_unavailable" ||
                state.kind === "no_certificates" ||
                state.kind === "certificate_not_found"
                  ? "Проверить снова"
                  : "Начать заново"}
              </button>
            )}
            <button
              className="button button--text button--compact"
              type="button"
              onClick={close}
            >
              Закрыть
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
